import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BarChart3, Sun, Moon, Globe, LogOut, User, ChevronDown } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useTheme from '../store/useTheme'
import useAuth from '../store/useAuth'
import api from '../services/api'

const Header = () => {
  const { dark, toggle } = useTheme()
  const navigate = useNavigate()
  const { isAdmin, logout, role } = useAuth()
  const [showMenu, setShowMenu] = useState(false)

  const handleLogout = async () => {
    setShowMenu(false)
    try {
      // Notify backend (best-effort, don't block on failure)
      await api.post('/api/auth/logout', {}, { timeout: 5000 }).catch(() => {})
    } finally {
      logout()
      toast.success('Déconnexion réussie')
      navigate('/login', { replace: true })
    }
  }

  return (
    <header className="sticky top-0 z-30 glass shadow-sm" style={{ height: 'var(--header-height)' }}>
      <div className="page-container h-full">
        <div className="flex items-center justify-between h-full">
          {/* Left: Logo + Title */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg shadow-sm">
                <BarChart3 className="w-4.5 h-4.5 text-white" />
              </div>
              <div className="flex items-baseline gap-1.5">
                <h1 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight">TradeScope</h1>
                <span className="hidden sm:inline text-xs font-medium text-gray-400 dark:text-gray-500">BI</span>
              </div>
            </div>
          </div>

          {/* Right: Status + Theme Toggle + User Menu */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 bg-gray-100/80 dark:bg-gray-800/80 px-2.5 py-1.5 rounded-full">
              <Globe className="w-3 h-3" />
              <span>En ligne</span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            </div>

            <button
              onClick={toggle}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
              aria-label="Basculer le thème"
            >
              {dark ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
            </button>

            {/* User menu with logout */}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300">
                  <User className="w-3.5 h-3.5" />
                </div>
                <span className="hidden sm:inline">
                  {isAdmin() ? 'Admin' : 'Utilisateur'}
                </span>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>

              {showMenu && (
                <>
                  {/* Click-outside overlay */}
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
                    <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800">
                      <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                        Compte
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-0.5">
                        {isAdmin() ? 'Administrateur' : 'Utilisateur'}
                      </p>
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                        Rôle: {role || 'user'}
                      </p>
                    </div>
                    <div className="p-1.5">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2 text-sm rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="font-medium">Se déconnecter</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
